import { AxiosResponse } from 'axios'
import { reportFrontendError } from '../../telemetry'

export const AUTH0_TOKEN_KEY = 'auth0-access-token'

type Auth0TokenGetter = () => Promise<string>
let auth0TokenGetter: Auth0TokenGetter | null = null
let refreshInFlight: Promise<string | null> | null = null

export const getAuth0RedirectUri = (): string =>
  `${window.location.origin}/auth/callback`

export const getAuth0LogoutReturnTo = (): string => window.location.origin

export function clearAuth0LocalStorage(): void {
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i)
    if (key && key.includes('auth0')) {
      localStorage.removeItem(key)
    }
  }
}

export function replaceBrowserHistoryWithAppRoot(): void {
  window.history.replaceState(null, '', '/')
}

export function prepareAuth0Logout(): void {
  clearAuth0LocalStorage()
  replaceBrowserHistoryWithAppRoot()
}

export const registerAuth0TokenGetter = (getter: Auth0TokenGetter): void => {
  auth0TokenGetter = getter
}

export const unregisterAuth0TokenGetter = (): void => {
  auth0TokenGetter = null
  refreshInFlight = null
}

export const refreshAuth0AccessToken = async (): Promise<string | null> => {
  if (!auth0TokenGetter) return null
  if (!refreshInFlight) {
    refreshInFlight = auth0TokenGetter()
      .then(token => {
        localStorage.setItem(AUTH0_TOKEN_KEY, token)
        return token
      })
      .catch(err => {
        reportFrontendError('api.error', err, {
          'auth.action': 'token_refresh',
        })
        return null
      })
      .finally(() => {
        refreshInFlight = null
      })
  }
  return refreshInFlight
}

const collectResponseErrorText = (
  response: AxiosResponse<ErrorResponse & { message?: string }>
): string => {
  const parts: string[] = []
  if (response.data?.message) {
    parts.push(response.data.message)
  }
  if (Array.isArray(response.data?.errors)) {
    parts.push(
      ...response.data.errors.filter(
        (error): error is string => typeof error === 'string'
      )
    )
  }
  return parts.join(' ').toLowerCase()
}

export const isInvalidAccessTokenResponse = (
  response?: AxiosResponse<ErrorResponse & { message?: string }>
): boolean => {
  if (!response) return false
  if (response.status === 401) return true
  return collectResponseErrorText(response).includes('invalid access token')
}
